import type { Streak } from '../shared/streak';
import type { DailySpec } from '../sim/daily';

/**
 * The site API client (design.md B14). Your driver lives in this browser:
 * an anonymous id + secret token, no password (B14.5). Submissions that
 * fail because you're offline are queued and retried later.
 */

export interface Profile {
  id: string;
  token: string;
  initials: string;
  color: number;
}

export interface RunSummary {
  date: string;
  status: 'ok' | 'dnf' | 'rejected' | 'started';
  ticks: number | null;
  splits: number[];
  rank: number | null;
  total: number;
}

export interface BoardRow {
  rank: number;
  id: string;
  initials: string;
  color: number;
  ticks: number;
}

export interface DailyInfo {
  date: string;
  today: string;
  spec: DailySpec;
  entries: number;
  top: BoardRow[];
  leaderSplits: number[];
  me: RunSummary | null;
  streak: Streak | null;
  yesterday: { date: string; spec: DailySpec; entries: number; top: BoardRow[] } | null;
}

export interface RecordsInfo {
  tracks: { id: string; top: { id: string; initials: string; color: number; team: string; ticks: number }[] }[];
}

export interface DriverCard {
  id: string;
  initials: string;
  color: number;
  created: number;
  streak: Streak;
  dailies: number;
  wins: number;
  podiums: number;
  bestRank: number | null;
  recent: (RunSummary & { number: number })[];
  laps: { track: string; team: string; ticks: number }[];
  ladder: LadderRecord;
}

export interface LadderChallenge {
  id: number;
  challenger: { id: string; initials?: string; color?: number; team: string };
  defender: { id: string | null; initials?: string; color?: number; team: string } | null;
  emptySeat: boolean;
  fromTier: number;
  toTier: number;
  track: string;
  state: 'setting' | 'open' | 'won' | 'defended' | 'cancelled';
  /** Ticks to beat (empty seat: benchmark; open: the challenger's lap). */
  target: number | null;
  challengerTicks: number | null;
  defenderTicks: number | null;
  deadline: number | null;
  expiresSetting: number | null;
  mine: 'challenger' | 'defender' | null;
  ghost: { start: GhostStartData; inputs: string; ticks: number } | null;
}

export interface GhostStartData {
  x: number;
  y: number;
  vx: number;
  vy: number;
  h: number;
  bonusMph: number;
  streakDist: number;
}

export interface LadderInfo {
  active: number;
  tiers: {
    tier: number;
    team: string;
    cap: number | null;
    open: number | null;
    count: number;
    drivers: { id: string; initials: string; color: number; since: number; busy: boolean; inactive: boolean }[];
  }[];
  me: { id: string; tier: number; team: string; busy: boolean; challenges: LadderChallenge[] } | null;
}

export interface LadderRecord {
  tier: number;
  team: string;
  won: number;
  lost: number;
  defended: number;
}

export type LadderAlert =
  | { kind: 'setting'; id: number; track: string }
  | { kind: 'defend'; id: number; track: string; by: string; deadline: number; target: number };

/** A lap in the Time Trial ghost format (verified by replay on the server). */
export interface LapUpload {
  ticks: number;
  start: GhostStartData;
  inputs: string;
}

/** Driver colours (helmet/livery accent on boards). */
export const DRIVER_COLORS = ['#ff5050', '#5a8cff', '#ffd21a', '#3fd25a', '#f2f2f2', '#c77dff', '#ff9a3c', '#3fd2d2'];

const KEY = 'sms.profile';
const PENDING = 'sms.pending';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: Record<string, unknown>,
  ) {
    super(String(body.error ?? `HTTP ${status}`));
  }
}

function lsGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}

function lsSet(k: string, v: string | null): void {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* private mode */
  }
}

export function loadProfile(): Profile | null {
  try {
    const p = JSON.parse(lsGet(KEY) ?? 'null') as Profile | null;
    return p && p.id && p.token ? p : null;
  } catch {
    return null;
  }
}

async function call<T>(method: string, path: string, body?: unknown, auth = true): Promise<T> {
  const p = loadProfile();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth && p) headers.Authorization = `Bearer ${p.token}`;
  const r = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  const json = (await r.json().catch(() => ({}))) as Record<string, unknown>;
  if (!r.ok) throw new ApiError(r.status, json);
  return json as T;
}

export const api = {
  async createProfile(initials: string, color: number): Promise<Profile> {
    const p = await call<Profile>('POST', '/api/profile', { initials, color }, false);
    lsSet(KEY, JSON.stringify(p));
    return p;
  },
  async updateProfile(initials: string, color: number): Promise<void> {
    await call('PATCH', '/api/profile', { initials, color });
    const p = loadProfile();
    if (p) lsSet(KEY, JSON.stringify({ ...p, initials, color }));
  },
  async deleteProfile(): Promise<void> {
    await call('DELETE', '/api/profile');
    lsSet(KEY, null);
  },
  me: () =>
    call<{ id: string; initials: string; color: number; streak: Streak; today: RunSummary | null; ladder: LadderRecord; ladderAlert: LadderAlert | null }>('GET', '/api/me'),
  // Same driver on another device: a 10-minute pairing code.
  linkCode: () => call<{ code: string; expires: number }>('POST', '/api/profile/link'),
  async claim(code: string): Promise<Profile> {
    const p = await call<Profile>('POST', '/api/profile/claim', { code }, false);
    lsSet(KEY, JSON.stringify(p));
    return p;
  },
  // The Seat Ladder.
  ladder: () => call<LadderInfo>('GET', '/api/ladder'),
  challenge: (defender: string | null) => call<LadderChallenge>('POST', '/api/ladder/challenge', { defender }),
  getChallenge: (id: number) => call<LadderChallenge>('GET', `/api/ladder/challenge/${id}`),
  challengeLap: (id: number, lap: LapUpload) => call<LadderChallenge>('POST', `/api/ladder/challenge/${id}/lap`, lap),
  sendChallenge: (id: number) => call<LadderChallenge>('POST', `/api/ladder/challenge/${id}/send`),
  cancelChallenge: (id: number) => call<LadderChallenge>('POST', `/api/ladder/challenge/${id}/cancel`),
  ghost: (driver: string, track: string, team: string) =>
    call<{ driver: string; initials: string; color: number; track: string; team: string; ticks: number; start: GhostStartData; inputs: string }>(
      'GET',
      `/api/ghost/${encodeURIComponent(driver)}/${encodeURIComponent(track)}/${encodeURIComponent(team)}`,
      undefined,
      false,
    ),
  daily: (date?: string) => call<DailyInfo>('GET', `/api/daily${date ? `?date=${date}` : ''}`),
  startDaily: (date: string) => call<{ ok: true; spec: DailySpec }>('POST', '/api/daily/start', { date }),
  submitDaily: (date: string, inputs: string, status: 'ok' | 'dnf', ticks: number | null) =>
    call<{ run: RunSummary; streak: Streak; leaderSplits: number[] }>('POST', '/api/daily/run', { date, inputs, status, ticks }),
  records: () => call<RecordsInfo>('GET', '/api/records', undefined, false),
  driver: (id: string) => call<DriverCard>('GET', `/api/driver/${encodeURIComponent(id)}`, undefined, false),
  async exportData(): Promise<Blob> {
    const p = loadProfile();
    const r = await fetch('/api/me/export', { headers: p ? { Authorization: `Bearer ${p.token}` } : {} });
    return r.blob();
  },
  /** Time Trial personal best → lap records (verified by replay on the server). */
  submitLap: (lap: { track: string; team: string; ticks: number; start: object; inputs: string }) =>
    call<{ ok: true; best: number; rank: number }>('POST', '/api/laps', lap),
};

// ------------------------------------------------------- offline queue

type LapPayload = Parameters<typeof api.submitLap>[0];
type DailyPayload = { date: string; inputs: string; status: 'ok' | 'dnf'; ticks: number | null };
type Pending = { kind: 'lap'; lap: LapPayload } | { kind: 'daily'; run: DailyPayload };

export function queueLap(lap: LapPayload): void {
  const q = pending().filter((p) => !(p.kind === 'lap' && p.lap.track === lap.track && p.lap.team === lap.team && p.lap.ticks >= lap.ticks));
  q.push({ kind: 'lap', lap });
  lsSet(PENDING, JSON.stringify(q.slice(-20)));
}

/** A ranked run that couldn't be handed in (offline): retried on the next visit. */
export function queueDaily(run: DailyPayload): void {
  const q = pending().filter((p) => !(p.kind === 'daily' && p.run.date === run.date));
  q.push({ kind: 'daily', run });
  lsSet(PENDING, JSON.stringify(q.slice(-20)));
}

export function dropQueuedDaily(date: string): void {
  lsSet(PENDING, JSON.stringify(pending().filter((p) => !(p.kind === 'daily' && p.run.date === date))));
}

function pending(): Pending[] {
  try {
    return JSON.parse(lsGet(PENDING) ?? '[]') as Pending[];
  } catch {
    return [];
  }
}

/** Try to send anything that failed while offline. */
export async function flushQueue(): Promise<void> {
  if (!loadProfile()) return;
  const q = pending();
  if (!q.length) return;
  const left: Pending[] = [];
  for (const p of q) {
    try {
      if (p.kind === 'lap') await api.submitLap(p.lap);
      else await api.submitDaily(p.run.date, p.run.inputs, p.run.status, p.run.ticks);
    } catch (e) {
      // Network trouble: keep it. A rejection (4xx) is final: drop it.
      if (!(e instanceof ApiError)) left.push(p);
    }
  }
  lsSet(PENDING, JSON.stringify(left));
}
